import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Save, Loader2, Search, X, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useAdminFilter } from "@/context/AdminFilterContext";

// Note: this panel intentionally has NO local date filter. Its date window
// is driven by the shared AdminFilter context so the top Profit Dashboard
// chips control every tracker in one go — merchant picks a period once and
// every panel updates consistently.

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const todayLocalIso = () => iso(new Date());

const CURRENCIES = ["GBP", "EUR", "USD", "NGN"];
const CURRENCY_SYMBOL = { GBP: "£", EUR: "€", USD: "$", NGN: "₦" };

/**
 * Generic finance-list panel used by both ExpensesTab and OtherIncomeTab.
 * Keeps the two feature panels visually + behaviourally identical without
 * duplicating ~300 lines of table/form code.
 */
export default function FinanceTrackerPanel({
  resource,                 // "expenses" | "other_income"
  eyebrow,                  // section eyebrow copy
  headline,                 // main heading
  helperText,               // small hint under headline
  categoryField,            // { key, label, options[] }
  descriptionField,         // { key, label, placeholder }
}) {
  const qc = useQueryClient();
  const { settings } = useSettings();
  const { params: range, filterLabel } = useAdminFilter();
  const [search, setSearch] = useState("");
  // Which month cards are open. Default: all collapsed — merchant sees the
  // month totals first, then taps to reveal individual entries. Keyed on
  // "YYYY-MM" (the same key used to group entries below).
  const [openMonths, setOpenMonths] = useState(() => new Set());
  const toggleMonth = (key) =>
    setOpenMonths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  // Draft form (creates new entries)
  const [draft, setDraft] = useState(() => ({
    date: todayLocalIso(),
    [categoryField.key]: categoryField.options[0],
    currency: "GBP",
    amount: "",
    [descriptionField.key]: "",
  }));
  const [saving, setSaving] = useState(false);

  const { data: allItems = [], isLoading } = useQuery({
    queryKey: [`admin-${resource}`],
    queryFn: () => api.get(`/admin/${resource}`).then((r) => r.data),
    staleTime: 10_000,
  });

  // ---- currency conversion (matches backend to_gbp/finance_rollup logic) ----
  const ngnRate = Number(settings?.ngn_rate_override) || 0;
  const eurRate = Number(settings?.eur_rate_override) || 0;
  const usdRate = Number(settings?.usd_rate_override) || 0;
  const toGbp = (amount, currency) => {
    const a = Number(amount) || 0;
    if (currency === "GBP") return a;
    if (currency === "EUR") return eurRate > 0 ? a / eurRate : 0;
    if (currency === "USD") return usdRate > 0 ? a / usdRate : 0;
    if (currency === "NGN") return ngnRate > 0 ? a / ngnRate : 0;
    return 0;
  };
  const fmtNgn = (v) => `₦${(Number(v) || 0).toLocaleString("en-NG", { maximumFractionDigits: 0 })}`;
  const fmtGbp = (v) => `£${(Number(v) || 0).toFixed(2)}`;
  const fmtEur = (v) => `€${(Number(v) || 0).toFixed(2)}`;

  // ---- date filter + free-text search ----
  const withinRange = (it) => {
    if (!range.date_from && !range.date_to) return true;
    const s = String(it.date || "").slice(0, 10);
    if (!s) return false;
    if (range.date_from && s < range.date_from) return false;
    if (range.date_to && s > range.date_to) return false;
    return true;
  };
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allItems
      .filter(withinRange)
      .filter((it) => {
        if (!q) return true;
        const hay = [it[categoryField.key], it[descriptionField.key], it.currency, it.amount, it.date]
          .map((v) => String(v ?? "").toLowerCase()).join(" | ");
        return hay.includes(q);
      })
      .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allItems, range.date_from, range.date_to, search]);

  // Group filtered entries by YYYY-MM so the merchant can eyeball month-level
  // spend without waiting for the Profit Dashboard summary to load.
  const groupedByMonth = useMemo(() => {
    const buckets = new Map();
    for (const it of filtered) {
      const key = String(it.date || "").slice(0, 7) || "unknown";
      if (!buckets.has(key)) buckets.set(key, { key, items: [], totalGbp: 0 });
      const b = buckets.get(key);
      b.items.push(it);
      b.totalGbp += toGbp(it.amount, it.currency);
    }
    return Array.from(buckets.values()).map((b) => ({
      ...b,
      label: b.key === "unknown"
        ? "No date"
        : new Date(`${b.key}-01T12:00:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" }),
      totalNgn: b.totalGbp * ngnRate,
      totalEur: b.totalGbp * eurRate,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, ngnRate, eurRate]);

  const rangeTotalGbp = filtered.reduce((s, it) => s + toGbp(it.amount, it.currency), 0);

  const commit = async () => {
    if (!draft.date || !draft.amount) return;
    setSaving(true);
    try {
      await api.post(`/admin/${resource}`, {
        ...draft,
        amount: Number(draft.amount) || 0,
      });
      setDraft({
        date: todayLocalIso(),
        [categoryField.key]: categoryField.options[0],
        currency: "GBP",
        amount: "",
        [descriptionField.key]: "",
      });
      qc.invalidateQueries({ queryKey: [`admin-${resource}`] });
      qc.invalidateQueries({ queryKey: ["admin-orders-report-profit"] });
    } finally {
      setSaving(false);
    }
  };

  const patch = async (id, changes) => {
    await api.put(`/admin/${resource}/${id}`, changes);
    qc.invalidateQueries({ queryKey: [`admin-${resource}`] });
    qc.invalidateQueries({ queryKey: ["admin-orders-report-profit"] });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry? This cannot be undone.")) return;
    await api.delete(`/admin/${resource}/${id}`);
    qc.invalidateQueries({ queryKey: [`admin-${resource}`] });
    qc.invalidateQueries({ queryKey: ["admin-orders-report-profit"] });
  };

  return (
    <div className="space-y-6" data-testid={`${resource}-tab`}>
      {/* Heading */}
      <div>
        <div className="text-[0.65rem] uppercase tracking-[0.28em] text-[#D4AF37]">{eyebrow}</div>
        <div className="font-serif text-2xl text-white mt-1">{headline}</div>
        <div className="text-white/50 text-sm mt-2 max-w-2xl">{helperText}</div>
      </div>

      {/* Add-entry form */}
      <div className="border border-[#2A2A2A] bg-[#0A0A0A] p-5" data-testid={`${resource}-add-form`}>
        <div className="text-[0.65rem] uppercase tracking-[0.22em] text-white/55 mb-3">Add new entry</div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div>
            <div className="text-[0.6rem] uppercase tracking-[0.2em] text-white/45 mb-1">Date</div>
            <input
              type="date"
              value={draft.date}
              onChange={(e) => setDraft({ ...draft, date: e.target.value })}
              className="w-full bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-white text-sm"
              data-testid={`${resource}-form-date`}
            />
          </div>
          <div>
            <div className="text-[0.6rem] uppercase tracking-[0.2em] text-white/45 mb-1">{categoryField.label}</div>
            <select
              value={draft[categoryField.key]}
              onChange={(e) => setDraft({ ...draft, [categoryField.key]: e.target.value })}
              className="w-full bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-white text-sm"
              data-testid={`${resource}-form-category`}
            >
              {categoryField.options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[0.6rem] uppercase tracking-[0.2em] text-white/45 mb-1">Currency</div>
            <select
              value={draft.currency}
              onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
              className="w-full bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-white text-sm"
              data-testid={`${resource}-form-currency`}
            >
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[0.6rem] uppercase tracking-[0.2em] text-white/45 mb-1">Amount ({CURRENCY_SYMBOL[draft.currency]})</div>
            <input
              type="number"
              step="0.01"
              value={draft.amount}
              onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
              placeholder="0.00"
              className="w-full bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-white text-sm"
              data-testid={`${resource}-form-amount`}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <div className="text-[0.6rem] uppercase tracking-[0.2em] text-white/45 mb-1">{descriptionField.label}</div>
            <input
              type="text"
              value={draft[descriptionField.key]}
              onChange={(e) => setDraft({ ...draft, [descriptionField.key]: e.target.value })}
              placeholder={descriptionField.placeholder}
              className="w-full bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-white text-sm"
              data-testid={`${resource}-form-description`}
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={commit}
              disabled={saving || !draft.date || !draft.amount}
              className="btn-gold w-full disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`${resource}-form-save`}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add
            </button>
          </div>
        </div>
      </div>

      {/* Filter bar — filter chips have moved to the shared Profit Dashboard.
          Only free-text search is per-tab. */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[0.6rem] uppercase tracking-[0.22em] text-white/45 mr-2">
          Showing: <span className="text-[#D4AF37]">{filterLabel}</span>
          {range.date_from && range.date_to && (
            <span className="text-white/40 ml-1">· {range.date_from} → {range.date_to}</span>
          )}
        </div>
        <div className="relative ml-auto min-w-[220px]">
          <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search entries…"
            className="w-full bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none pl-9 pr-8 py-2 text-white text-sm"
            data-testid={`${resource}-search`}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-white/50 hover:text-[#D4AF37] p-1"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Range total badge */}
      <div className="grid grid-cols-3 gap-3" data-testid={`${resource}-range-totals`}>
        <div className="border border-[#2A2A2A] bg-[#0F0F0F] p-4">
          <div className="text-[0.6rem] uppercase tracking-[0.22em] text-white/45">Range total (NGN)</div>
          <div className="font-serif text-xl text-white mt-1" data-testid={`${resource}-range-ngn`}>{fmtNgn(rangeTotalGbp * ngnRate)}</div>
        </div>
        <div className="border border-[#2A2A2A] bg-[#0F0F0F] p-4">
          <div className="text-[0.6rem] uppercase tracking-[0.22em] text-white/45">Range total (GBP)</div>
          <div className="font-serif text-xl text-white mt-1" data-testid={`${resource}-range-gbp`}>{fmtGbp(rangeTotalGbp)}</div>
        </div>
        <div className="border border-[#2A2A2A] bg-[#0F0F0F] p-4">
          <div className="text-[0.6rem] uppercase tracking-[0.22em] text-white/45">Range total (EUR)</div>
          <div className="font-serif text-xl text-white mt-1" data-testid={`${resource}-range-eur`}>{fmtEur(rangeTotalGbp * eurRate)}</div>
        </div>
      </div>

      {/* Monthly grouped entries */}
      {isLoading ? (
        <div className="text-white/40 py-6">Loading…</div>
      ) : groupedByMonth.length === 0 ? (
        <div className="text-center py-14 border border-[#2A2A2A] bg-[#0A0A0A]" data-testid={`${resource}-empty`}>
          <div className="font-serif text-lg text-white">No entries in this range yet.</div>
          <p className="text-white/50 text-sm mt-2">Use the form above to log your first entry.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groupedByMonth.map((m) => {
            const isOpen = openMonths.has(m.key);
            return (
              <div key={m.key} className="border border-[#2A2A2A] bg-[#0A0A0A]" data-testid={`${resource}-month-${m.key}`}>
                <button
                  type="button"
                  onClick={() => toggleMonth(m.key)}
                  aria-expanded={isOpen}
                  aria-controls={`${resource}-month-body-${m.key}`}
                  className="w-full px-5 py-3 flex flex-wrap items-center justify-between gap-3 text-left hover:bg-[#0F0F0F] transition-colors"
                  data-testid={`${resource}-month-toggle-${m.key}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <ChevronDown
                      className={`w-4 h-4 text-[#D4AF37] shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                      aria-hidden
                    />
                    <div className="font-serif text-lg text-white">{m.label}</div>
                  </div>
                  <div className="flex flex-wrap items-baseline gap-4 text-sm">
                    <div className="text-white/60" data-testid={`${resource}-month-count-${m.key}`}>
                      {m.items.length} entr{m.items.length === 1 ? "y" : "ies"}
                    </div>
                    <div className="text-[#D4AF37] font-semibold tabular-nums" data-testid={`${resource}-month-total-ngn-${m.key}`}>{fmtNgn(m.totalNgn)}</div>
                    <div className="text-white/60 tabular-nums">{fmtGbp(m.totalGbp)}</div>
                    <div className="text-white/60 tabular-nums">{fmtEur(m.totalEur)}</div>
                  </div>
                </button>
                {isOpen && (
                  <div id={`${resource}-month-body-${m.key}`} className="divide-y divide-[#1F1F1F] border-t border-[#2A2A2A]">
                    {m.items.map((it) => (
                      <FinanceRow
                        key={it.id}
                        item={it}
                        categoryField={categoryField}
                        descriptionField={descriptionField}
                        onPatch={(changes) => patch(it.id, changes)}
                        onDelete={() => remove(it.id)}
                        toGbp={toGbp}
                        ngnRate={ngnRate}
                        eurRate={eurRate}
                        resource={resource}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FinanceRow({ item, categoryField, descriptionField, onPatch, onDelete, toGbp, ngnRate, eurRate, resource }) {
  const [draft, setDraft] = useState({
    date: item.date || "",
    [categoryField.key]: item[categoryField.key] || categoryField.options[0],
    currency: item.currency || "GBP",
    amount: item.amount ?? "",
    [descriptionField.key]: item[descriptionField.key] || "",
  });
  const [saving, setSaving] = useState(false);

  const dirty =
    draft.date !== (item.date || "") ||
    draft[categoryField.key] !== (item[categoryField.key] || categoryField.options[0]) ||
    draft.currency !== (item.currency || "GBP") ||
    Number(draft.amount) !== Number(item.amount ?? 0) ||
    draft[descriptionField.key] !== (item[descriptionField.key] || "");

  const gbp = toGbp(item.amount, item.currency);

  const save = async () => {
    setSaving(true);
    try {
      await onPatch({ ...draft, amount: Number(draft.amount) || 0 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-5 py-3 grid grid-cols-2 md:grid-cols-7 gap-3 items-center" data-testid={`${resource}-row-${item.id}`}>
      <input
        type="date"
        value={draft.date}
        onChange={(e) => setDraft({ ...draft, date: e.target.value })}
        className="bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-2 py-1.5 text-white text-xs"
      />
      <select
        value={draft[categoryField.key]}
        onChange={(e) => setDraft({ ...draft, [categoryField.key]: e.target.value })}
        className="bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-2 py-1.5 text-white text-xs"
      >
        {categoryField.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      <select
        value={draft.currency}
        onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
        className="bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-2 py-1.5 text-white text-xs"
      >
        {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <input
        type="number"
        step="0.01"
        value={draft.amount}
        onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
        className="bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-2 py-1.5 text-white text-xs tabular-nums"
      />
      <input
        type="text"
        value={draft[descriptionField.key]}
        onChange={(e) => setDraft({ ...draft, [descriptionField.key]: e.target.value })}
        className="col-span-2 md:col-span-1 bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-2 py-1.5 text-white text-xs"
      />
      <div className="text-right text-[0.7rem] text-white/55 tabular-nums leading-tight">
        <div>₦{Math.round(gbp * ngnRate).toLocaleString("en-NG")}</div>
        <div>£{gbp.toFixed(2)}</div>
        <div>€{(gbp * eurRate).toFixed(2)}</div>
      </div>
      <div className="flex items-center justify-end gap-2">
        {dirty && (
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="btn-outline text-xs px-3 py-1.5 flex items-center gap-1"
            data-testid={`${resource}-row-save-${item.id}`}
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Save
          </button>
        )}
        <button
          type="button"
          onClick={onDelete}
          className="text-white/40 hover:text-red-400 p-1"
          aria-label="Delete entry"
          data-testid={`${resource}-row-delete-${item.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
